import React from 'react';
import PublicLayout from '../../components/layouts/PublicLayout';
import { usePublicTranslation } from '../../hooks/usePublicTranslation';
import { useSEO, generateBreadcrumbSchema, generateWebPageSchema } from '../../hooks/useSEO';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy: React.FC = () => {
    const { t, currentLanguage } = usePublicTranslation();
    const legal = t.legal || {};
    const content = legal.privacyContent || {};

    useSEO({
        title: legal.seo?.privacy?.title || "Politica de Privacidade - NexuCapital",
        description: legal.seo?.privacy?.description || "Saiba como a NexuCapital coleta, usa e protege seus dados pessoais.",
        path: '/privacy-policy',
        language: currentLanguage as 'pt-br' | 'en' | 'es',
        noindex: false,
        structuredData: [
            generateBreadcrumbSchema([
                { name: 'Home', url: '/' },
                { name: 'Legal', url: '/legal' },
                { name: 'Privacy Policy', url: '/privacy-policy' }
            ]),
            generateWebPageSchema({
                name: legal.seo?.privacy?.title || "Politica de Privacidade - NexuCapital",
                description: legal.seo?.privacy?.description || "Saiba como a NexuCapital coleta, usa e protege seus dados pessoais.",
                url: '/privacy-policy',
                datePublished: "2025-01-20",
                dateModified: "2025-01-20"
            })
        ]
    });

    return (
        <PublicLayout>

            <section className="pt-24 pb-16 bg-gray-50 min-h-screen">
                <div className="container mx-auto px-4">
                    {/* Back Link */}
                    <a
                        href="/"
                        className="inline-flex items-center text-primary hover:text-primary-dark mb-6 transition-colors"
                    >
                        <ArrowLeft size={18} className="mr-2" />
                        {t.navigation?.home || "Inicio"}
                    </a>

                    {/* Content Card */}
                    <div className="bg-white rounded-xl shadow-sm p-6 sm:p-8 md:p-12 max-w-4xl mx-auto">
                        {/* Header */}
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-primary mb-4 pb-4 border-b-2 border-secondary">
                            {legal.privacyPolicy || "Politica de Privacidade"}
                        </h1>

                        <p className="text-gray-600 mb-8">
                            <strong>{legal.lastUpdated || "Ultima atualizacao"}:</strong> {legal.lastUpdatedDate || "01 de setembro de 2025"}
                        </p>

                        {/* Intro */}
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.intro}
                        </p>

                        {/* Section 1 - DPO */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section1Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            {content.section1Text}
                        </p>
                        <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-8">
                            {content.section1Items?.map((item: string, index: number) => (
                                <li key={index}>{item}</li>
                            ))}
                        </ul>

                        {/* Section 2 - Data We Collect */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section2Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            {content.section2Text}
                        </p>
                        <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-8">
                            {content.section2Items?.map((item: string, index: number) => {
                                const colonIndex = item.indexOf(':');
                                if (colonIndex > -1) {
                                    return (
                                        <li key={index}>
                                            <strong>{item.substring(0, colonIndex + 1)}</strong>
                                            {item.substring(colonIndex + 1)}
                                        </li>
                                    );
                                }
                                return <li key={index}>{item}</li>;
                            })}
                        </ul>

                        {/* Section 3 - Legal Bases */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section3Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            {content.section3Text}
                        </p>
                        <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-8">
                            {content.section3Items?.map((item: string, index: number) => {
                                const colonIndex = item.indexOf(':');
                                if (colonIndex > -1) {
                                    return (
                                        <li key={index}>
                                            <strong>{item.substring(0, colonIndex + 1)}</strong>
                                            {item.substring(colonIndex + 1)}
                                        </li>
                                    );
                                }
                                return <li key={index}>{item}</li>;
                            })}
                        </ul>

                        {/* Section 4 - Processing Purposes */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section4Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            {content.section4Text}
                        </p>
                        <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-8">
                            {content.section4Items?.map((item: string, index: number) => {
                                const colonIndex = item.indexOf(':');
                                if (colonIndex > -1) {
                                    return (
                                        <li key={index}>
                                            <strong>{item.substring(0, colonIndex + 1)}</strong>
                                            {item.substring(colonIndex + 1)}
                                        </li>
                                    );
                                }
                                return <li key={index}>{item}</li>;
                            })}
                        </ul>

                        {/* Section 5 - Data Sharing */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section5Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            {content.section5Text}
                        </p>
                        <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-8">
                            {content.section5Items?.map((item: string, index: number) => {
                                const colonIndex = item.indexOf(':');
                                if (colonIndex > -1) {
                                    return (
                                        <li key={index}>
                                            <strong>{item.substring(0, colonIndex + 1)}</strong>
                                            {item.substring(colonIndex + 1)}
                                        </li>
                                    );
                                }
                                return <li key={index}>{item}</li>;
                            })}
                        </ul>

                        {/* Section 6 - International Transfer */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section6Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section6Text}
                        </p>

                        {/* Section 7 - Data Security */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section7Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            {content.section7Text}
                        </p>
                        <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-8">
                            {content.section7Items?.map((item: string, index: number) => (
                                <li key={index}>{item}</li>
                            ))}
                        </ul>

                        {/* Section 8 - Data Retention */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section8Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            {content.section8Text}
                        </p>
                        <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-4">
                            {content.section8Items?.map((item: string, index: number) => {
                                const colonIndex = item.indexOf(':');
                                if (colonIndex > -1) {
                                    return (
                                        <li key={index}>
                                            <strong>{item.substring(0, colonIndex + 1)}</strong>
                                            {item.substring(colonIndex + 1)}
                                        </li>
                                    );
                                }
                                return <li key={index}>{item}</li>;
                            })}
                        </ul>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section8Text2}
                        </p>

                        {/* Section 9 - Your Rights */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section9Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            {content.section9Text}
                        </p>
                        <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-4">
                            {content.section9Items?.map((item: string, index: number) => {
                                const colonIndex = item.indexOf(':');
                                if (colonIndex > -1) {
                                    return (
                                        <li key={index}>
                                            <strong>{item.substring(0, colonIndex + 1)}</strong>
                                            {item.substring(colonIndex + 1)}
                                        </li>
                                    );
                                }
                                return <li key={index}>{item}</li>;
                            })}
                        </ul>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section9Text2}
                        </p>

                        {/* Section 10 - Changes */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section10Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed">
                            {content.section10Text}
                        </p>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
};

export default PrivacyPolicy;
